<template>
  <div
    class="two-image-block critical-component-clear liquid"
    :style="liquidStyles"
  >
    <ul class="grid">
      <li
        v-for="(block, index) of blockData"
        :key="index"
        class="col xs6"
      >
        <div
          role="group"
          :aria-label="$string('accessibility.block_labels.two_image_block')"
        >
          <component
            :is="getComponentConfig(block).is"
            v-bind="getComponentConfig(block).props"
          >
            <div class="two-image-block__image-wrapper">
              <template v-if="hasVideo(block)">
                <a
                  v-if="block.imageUrl"
                  :href="block.imageUrl"
                  class="two-image-block__video-link"
                  tabindex="-1"
                  :aria-label="$string('accessibility.block_labels.two_image_block')"
                >
                  <video-player
                    class="two-image-block__video"
                    :video="getVideoObject(block)"
                    :image="block.imageMobile"
                    :is-autoplay="block.videoAutoplay === 'true'"
                    :loop="block.videoLoop === 'true'"
                    :muted="block.videoSound !== 'true'"
                    :icon-color="block.videoPlayerColor || 'black'"
                    :use-overlay-button="false"
                  />
                </a>
                <video-player
                  v-else
                  class="two-image-block__video"
                  :video="getVideoObject(block)"
                  :image="block.imageMobile"
                  :is-autoplay="block.videoAutoplay === 'true'"
                  :loop="block.videoLoop === 'true'"
                  :muted="block.videoSound !== 'true'"
                  :icon-color="block.videoPlayerColor || 'black'"
                  :use-overlay-button="false"
                />
              </template>
              <art-direction
                v-else-if="block.gifUrl"
                :image="block.gifUrl"
                :image-desktop="block.gifUrl"
                :ratio="179/156"
                :ratio-desktop="59/69"
                class="two-image-block__gif"
              />
              <art-direction
                v-else
                :image="block.imageMobile"
                :image-desktop="block.imageDesktop"
                :ratio="179/156"
                :ratio-desktop="59/69"
              />
              <!-- Title Overlay -->
              <div
                class="two-image-block__title-overlay"
                :class="[
                  `two-image-block__title-overlay--${block.titlePosition
                    || 'left'}`,
                  `two-image-block__title-overlay--${block.titleColor
                    || 'black'}`,
                  hasVideo(block) && (block.titlePosition
                    || 'left').includes('bottom') ?
                    'two-image-block__overlay--video-bottom' : ''
                ]"
                :style="block.titleColor === 'custom'
                  && block.titleCustomColor ? { color: block.titleCustomColor } : {}"
              >
                <h2 class="two-image-block__title text-heading-m">
                  {{ block.title }}
                </h2>
              </div>
              <!-- Subtitle Overlay -->
              <div
                class="two-image-block__subtitle-overlay"
                :class="[
                  `two-image-block__subtitle-overlay--${block.subtitlePosition
                    || 'left'}`,
                  `two-image-block__subtitle-overlay--${block.subtitleColor
                    || 'black'}`,
                  hasVideo(block) && (block.subtitlePosition
                    || 'left').includes('bottom')
                    ? 'two-image-block__overlay--video-bottom' : ''
                ]"
                :style="block.subtitleColor === 'custom' && block.subtitleCustomColor
                  ? { color: block.subtitleCustomColor }
                  : {}"
              >
                <p class="two-image-block__subtitle">
                  {{ block.subtitle }}
                </p>
              </div>
              <!-- Description Overlay -->
              <div
                class="two-image-block__description-overlay"
                :class="[
                  `two-image-block__description-overlay--${block.descriptionPosition
                    || 'left'}`,
                  `two-image-block__description-overlay--${block.descriptionColor
                    || 'black'}`,
                  hasVideo(block) && (block.descriptionPosition
                    || 'left').includes('bottom')
                    ? 'two-image-block__overlay--video-bottom' : ''
                ]"
                :style="block.descriptionColor === 'custom' &&
                  block.descriptionCustomColor ? { color: block.descriptionCustomColor } : {}"
              >
                <p class="two-image-block__description">
                  {{ block.description }}
                </p>
              </div>
              <!-- CTA One Overlay -->

              <div
                v-if="block.ctaLabelOne && block.ctaUrlOne"
                class="two-image-block__cta-one-overlay"
                :class="[
                  `two-image-block__cta-one-overlay--${block.ctaOnePosition
                    || 'left'}`,
                  `two-image-block__cta-one-overlay--${block.ctaOneColor
                    || 'black'}`,
                  hasVideo(block) && (block.ctaOnePosition
                    || 'left').includes('bottom') ?
                    'two-image-block__overlay--video-bottom' : ''
                ]"
              >
                <a
                  :href="block.ctaUrlOne"
                  class="two-image-block__cta-one-link"
                  :class="[
                    {
                      'button two-image-block__cta-link--button': block.ctaOneButton === 'button',
                      'has-custom-bg': block.ctaOneButton === 'button' &&
                        block.ctaOneButtonBg === 'custom'
                    },
                    block.ctaOneButton === 'button' && block.ctaOneButtonBg === 'custom'
                      ? 'two-image-block__cta-link--bg-custom'
                      : block.ctaOneButton === 'button' && block.ctaOneButtonBg === 'black'
                        ? 'two-image-block__cta-link--bg-black'
                        : block.ctaOneButton === 'button' && block.ctaOneButtonBg === 'white'
                          ? 'two-image-block__cta-link--bg-white'
                          : ''
                  ]"
                  :style="{
                    ...(block.ctaOneButton === 'button' &&
                      (block.ctaOneButtonBg === 'custom' ||
                        block.ctaOneButtonBg === 'black') &&
                      block.ctaOneCustomBgColor
                      ? { '--cta-one-bg': block.ctaOneCustomBgColor }
                      : {}),
                    ...(block.ctaOneButton === 'button' &&
                      (block.ctaOneButtonBg === 'custom' ||
                        block.ctaOneButtonBg === 'black') &&
                      block.ctaOneCustomBgHoverColor
                      ? { '--cta-one-hover-bg': block.ctaOneCustomBgHoverColor }
                      : {}),
                    ...(block.ctaOneColor === 'custom' && block.ctaOneCustomColor
                      ? { color: block.ctaOneCustomColor, borderColor: block.ctaOneCustomColor }
                      : {}),
                    ...(block.ctaOneColor === 'custom' && block.ctaOneCustomHoverColor
                      ? { '--cta-one-hover-color': block.ctaOneCustomHoverColor }
                      : {})
                  }"
                >
                  {{ block.ctaLabelOne }}
                </a>
              </div>

              <!-- CTA Two Overlay -->
              <div
                v-if="block.ctaLabelTwo && block.ctaUrlTwo"
                class="two-image-block__cta-two-overlay"
                :class="[
                  `two-image-block__cta-two-overlay--${block.ctaTwoPosition
                    || 'left'}`,
                  `two-image-block__cta-two-overlay--${block.ctaTwoColor
                    || 'black'}`,
                  hasVideo(block) && (block.ctaTwoPosition
                    || 'left').includes('bottom') ?
                    'two-image-block__overlay--video-bottom' : ''
                ]"
              >
                <a
                  :href="block.ctaUrlTwo"
                  class="two-image-block__cta-two-link"
                  :class="[
                    {
                      'button two-image-block__cta-link--button': block.ctaTwoButton === 'button',
                      'has-custom-bg': block.ctaTwoButton === 'button' &&
                        block.ctaTwoButtonBg === 'custom'
                    },
                    block.ctaTwoButton === 'button' && block.ctaTwoButtonBg === 'custom'
                      ? 'two-image-block__cta-link--bg-custom'
                      : block.ctaTwoButton === 'button' && block.ctaTwoButtonBg === 'black'
                        ? 'two-image-block__cta-link--bg-black'
                        : block.ctaTwoButton === 'button' && block.ctaTwoButtonBg === 'white'
                          ? 'two-image-block__cta-link--bg-white'
                          : ''
                  ]"
                  :style="{
                    ...(block.ctaTwoButton === 'button' &&
                      (block.ctaTwoButtonBg === 'custom' ||
                        block.ctaTwoButtonBg === 'black') &&
                      block.ctaTwoCustomBgColor
                      ? { '--cta-two-bg': block.ctaTwoCustomBgColor }
                      : {}),
                    ...(block.ctaTwoButton === 'button' &&
                      (block.ctaTwoButtonBg === 'custom' ||
                        block.ctaTwoButtonBg === 'black') &&
                      block.ctaTwoCustomBgHoverColor
                      ? { '--cta-two-hover-bg': block.ctaTwoCustomBgHoverColor }
                      : {}),
                    ...(block.ctaTwoColor === 'custom' && block.ctaTwoCustomColor
                      ? { color: block.ctaTwoCustomColor, borderColor: block.ctaTwoCustomColor }
                      : {}),
                    ...(block.ctaTwoColor === 'custom' && block.ctaTwoCustomHoverColor
                      ? { '--cta-two-hover-color': block.ctaTwoCustomHoverColor }
                      : {})
                  }"
                >
                  {{ block.ctaLabelTwo }}
                </a>
              </div>
            </div>
          </component>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import VideoPlayer from '~async/video-player/video-player'
import ArtDirection from '~global/images/art-direction'

export default {
  name: 'TwoImageBlock',
  components: {
    VideoPlayer,
    ArtDirection,
  },
  props: {
    blockData: {
      type: Array,
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    liquidStyles() {
      return {
        '--bottom-margin-mobile': this.liquid?.bottomMarginMobile
          ? `${this.liquid.bottomMarginMobile}px`
          : null,
        '--bottom-margin-desktop': this.liquid?.bottomMarginDesktop
          ? `${this.liquid.bottomMarginDesktop}px`
          : null,
      }
    },
  },

  methods: {
    hasVideo(block) {
      return Boolean(block.videoDesktop || block.videoMobile)
    },
    getVideoObject(block) {
      const isMobile = window.innerWidth <= 768
      const videoObj = isMobile ? block.videoMobile : block.videoDesktop
      if (videoObj?.video?.sources?.length) {
        const mp4Source =
          videoObj.video.sources.find(src => src.format === 'mp4')
        if (mp4Source) {
          return {
            ...videoObj.video,
            url: mp4Source.url,
            mimeType: 'video/mp4',
          }
        }
      }
      return null
    },
    getComponentConfig(block) {
      if (block.imageUrl && !this.hasVideo(block)) {
        return {
          is: 'a',
          props: {
            href: block.imageUrl,
            class: 'two-image-block__image-link',
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }
      }
      return {
        is: 'div',
        props: {
          tabindex: 0,
          class: 'two-image-block__image-link',
        },
      }
    },
  },
}
</script>

<style lang="scss">
@import './two-image-block';
</style>
